import axios from 'axios';

// Create an instance of axios with default config
const api = axios.create({
  baseURL: 'https://magellan-language-app-0c7c1a470fe3.herokuapp.com/api',
  //baseURL: 'http://127.0.0.1:5000/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add a request interceptor to include the auth token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only redirect for auth errors on non-session endpoints
    if (error.response && error.response.status === 401) {
      // Don't immediately redirect for session-related endpoints
      if (!error.config.url.includes('session')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // If we're not already on the login page, redirect
        if (window.location.pathname !== '/login') {
          // Use a timeout to prevent immediate redirects
          console.log('Auth error detected, redirecting to login in 2 seconds');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        }
      } else {
        console.log('Auth error on session endpoint - not redirecting automatically');
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API
const auth = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (userData) => api.put('/auth/profile', userData),
  getInterests: () => api.get('/auth/interests')
};

// Content API
const content = {
  generateIdeas: (params) => api.post('/generate-ideas', params),
  startSession: (params) => api.post('/start-session', params),
  continueSession: (data) => {
    return api.post('/continue-session', {
      session_id: data.session_id,
      user_input: data.user_input,
      input_type: data.input_type || 'text',
      system_note: data.system_note || null,
      audio_id: data.audio_id || null,  // Add this line to include audio_id
      action: data.action || null,  // #8: conversation scene action
      action_detail: data.action_detail || null  // free-text scenario for action='custom'
    });
  },
  resumeSession: (sessionId) => api.get(`/resume-session/${sessionId}`),
  getUserSessions: (params) => api.get('/user-sessions',params),
  getUserSessionsByLanguage: (params) => api.get('/user-sessions-language',params),
  deleteSession: (sessionId) => api.delete(`/delete-session/${sessionId}`)
};

// Vocabulary API
const vocabulary = {
  getVocabulary: (params) => api.get('/vocabulary', { params }),
  addVocabularyItem: (item) => api.post('/vocabulary', item),
  updateVocabularyItem: (id, item) => api.put(`/vocabulary/${id}`, item),
  deleteVocabularyItem: (id) => api.delete(`/vocabulary/${id}`),
  getVocabularyStats: () => api.get('/vocabulary/stats'),
  bulkUpdateVocabulary: (items) => api.put('/vocabulary/bulk', { items })
};

// Audio API
const audio = {
  generateAudio: (params) => api.post('/generate-audio', params),
  getAudioStatus: (jobId) => api.get(`/audio-status/${jobId}`),
  getAudioUrl: (jobId) => `https://magellan-language-app-0c7c1a470fe3.herokuapp.com/api/audio/${jobId}`
};

// New Sessions API for reviewing sessions
const sessions = {
  getUserSessions: () => api.get('/sessions'),
  getSession: (sessionId) => api.get(`/sessions/${sessionId}`),
  getSessionTranscript: (sessionId) => api.get(`/sessions/${sessionId}/transcript`),
  getLearningSummary: (sessionId) => api.get(`/sessions/${sessionId}/learning-summary`),
  addWordsToVocabulary: (sessionId, words) => api.post(`/sessions/${sessionId}/add-to-vocabulary`, words),
  getSessionAudioLinks: (sessionId) => api.get(`/sessions/${sessionId}/audio-links`),
  generateTranscriptAudio: (sessionId) => api.post(`/sessions/${sessionId}/generate-transcript-audio`),
  generateEntryAudio: (sessionId, order) => api.post(`/sessions/${sessionId}/transcript/${order}/audio`),
  checkAudioStatus: (sessionId) => api.get(`/sessions/${sessionId}/audio-status`),
  setVoiceGender: (sessionId, gender) => api.post(`/sessions/${sessionId}/voice-gender`, { gender })
};

const speechService = {
  // Start a speech recognition session
  startSpeechRecognition: (data) => {
    return axios.post('/api/speech/start', data);
  },

  // Get the status of a speech recognition session
  getSpeechStatus: (streamingId) => {
    return axios.get(`/api/speech/status/${streamingId}`);
  },

  // Close a speech recognition session
  closeSpeechSession: (streamingId) => {
    return axios.post(`/api/speech/close/${streamingId}`);
  },

  // Upload audio for transcription
  uploadSpeechAudio: (formData) => {
    return axios.post('/api/speech/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

// Speech recognition services
const audioService = {
  // Start a speech recognition session
  startSpeechRecognition: (data) => api.post('/speech/start', data),

  // Get the status of a speech recognition session
  getSpeechStatus: (streamingId) => api.get(`/speech/status/${streamingId}`),

  // Close a speech recognition session
  closeSpeechSession: (streamingId) => api.post(`/speech/close/${streamingId}`),

  // Upload audio for transcription
  uploadSpeechAudio: (formData) => api.post('/speech/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),

  transcribeWhisper: (formData) => {
      return api.post('/whisper/transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },

    // Transcribe audio data using Whisper API
    transcribeWhisperData: (audioData, sessionId, language) => {
      return api.post('/whisper/transcribe-data', {
        audio_data: audioData,
        sessionId,
        language
      });
    },

    // Check Whisper transcription status
    getWhisperStatus: (jobId) => {
      return api.get(`/whisper/status/${jobId}`);
    },

  // Re-use the existing audio methods
  generateAudio: (params) => api.post('/generate-audio', params),
  getAudioStatus: (jobId) => api.get(`/audio-status/${jobId}`),
  getAudioUrl: (jobId) => `${api.defaults.baseURL}/audio/${jobId}`
};

// Admin service
const admin = {
  // Authorization
  checkAuthorization: () => api.post('/admin/check-authorization'),

  // User management
  getUsers: () => api.get('/admin/users'),
  updateUserRole: (userId, roleData) => api.patch(`/admin/users/${userId}/role`, roleData),

  // Path management
  getPaths: () => api.get('/admin/paths'),
  createPath: (pathData) => api.post('/admin/paths', pathData),
  getPath: (pathId) => api.get(`/admin/paths/${pathId}`),
  updatePath: (pathId, pathData) => api.put(`/admin/paths/${pathId}`, pathData),
  deletePath: (pathId) => api.delete(`/admin/paths/${pathId}`),

  // Story management
  getPathStories: (pathId) => api.get(`/admin/paths/${pathId}/stories`),
  // All stories across every path in one request (no N+1).
  getAllStories: () => api.get('/admin/stories'),
  // Generate draft chapter content in the path's reference language (does not
  // persist; returns { title, content, language_code } for the author to edit).
  generateStoryDraft: (params) => api.post('/admin/stories/generate-draft', params),
  createStory: (storyData) => api.post('/admin/stories', storyData),
  getStory: (storyId) => api.get(`/admin/stories/${storyId}`),
  updateStory: (storyId, storyData) => api.put(`/admin/stories/${storyId}`, storyData),
  deleteStory: (storyId) => api.delete(`/admin/stories/${storyId}`),

  // Story content management
  generateTranslations: (storyId, targetLanguages) =>
    api.post(`/admin/stories/${storyId}/generate-translations`, { target_languages: targetLanguages }),
  updateTranslation: (storyId, language, contentData) =>
    api.put(`/admin/stories/${storyId}/translations/${language}`, contentData),
  deleteTranslation: (storyId, language) =>
    api.delete(`/admin/stories/${storyId}/translations/${language}`),
  generateAudio: (storyId, languages = null, voiceSettings = {}) =>
  api.post(`/admin/stories/${storyId}/generate-audio`, {
    languages,
    ...voiceSettings  // This spreads voice_gender and voice_quality
  }),
  publishStory: (storyId, force = false) =>
    api.post(`/admin/stories/${storyId}/publish`, { force_publish: force }),
  publishPath: (pathId, force = false) =>
    api.post(`/admin/paths/${pathId}/publish`, { force_publish: force }),

  // --- Content ops (overhaul): the matrix powers a path-level view; batch
  // operations are orchestrated client-side over the existing per-story
  // endpoints (generateAudio / asyncTranslation) with progress. ---
  getPathMatrix: (pathId) => api.get(`/admin/paths/${pathId}/matrix`),
  getAudioCoverage: (params = {}) => api.get('/admin/audio/coverage', { params }),
  reorderStories: (pathId, orderedStoryIds) =>
    api.post(`/admin/paths/${pathId}/reorder`, { ordered_story_ids: orderedStoryIds }),
  previewStory: (storyId, language) =>
    api.get(`/admin/stories/${storyId}/preview`, { params: { language_code: language } }),

  // --- Word matching (per-sentence English + tappable words) ---
  getWordMatching: (storyId, language) =>
    api.get(`/admin/stories/${storyId}/translations/${language}/word-matching`),
  saveWordMatching: (storyId, language, annotations) =>
    api.put(`/admin/stories/${storyId}/translations/${language}/word-matching`, { annotations }),
  clearWordMatching: (storyId, language) =>
    api.delete(`/admin/stories/${storyId}/translations/${language}/word-matching`),
  // Builds ONE ~40-word chunk per call (Heroku 30s limit); loop until `done`.
  buildWordMatching: (storyId, language, { chunk = null, reset = false } = {}) =>
    api.post(`/admin/stories/${storyId}/translations/${language}/word-matching/build`,
      chunk === null ? { reset } : { chunk, reset }),

  // --- Library management (Discover + Journal idea/prompt library) ---
  getLibrary: (kind = null) => api.get('/admin/library', { params: kind ? { kind } : {} }),
  createLibraryItem: (data) => api.post('/admin/library', data),
  updateLibraryItem: (itemId, data) => api.put(`/admin/library/${itemId}`, data),
  deleteLibraryItem: (itemId) => api.delete(`/admin/library/${itemId}`),

  asyncTranslation: {
  start: (storyId, targetLanguages, forceUpdate = false) =>
    api.post('/async-translation/start', {
      story_id: storyId,
      target_languages: targetLanguages,
      force_update: forceUpdate
    }),

  getStatus: (jobId) =>
    api.get(`/async-translation/status/${jobId}`),

  getStoryJobs: (storyId) =>
    api.get(`/async-translation/jobs/story/${storyId}`),

  cleanupOldJobs: () =>
    api.delete('/async-translation/jobs/cleanup')
}
};


// Breadcrumbs API
const breadcrumbs = {
  // Get all available paths - NOW WITH LANGUAGE
  getPaths: (languageCode) => api.get('/breadcrumbs/paths', {
    params: { language_code: languageCode }
  }),

  // Get user's recent paths - NOW WITH LANGUAGE
  getRecentPaths: (languageCode) => api.get('/breadcrumbs/paths/recent', {
    params: { language_code: languageCode }
  }),

  // Get specific path details with its stories - NOW WITH LANGUAGE
  getPath: (pathId, languageCode) => api.get(`/breadcrumbs/paths/${pathId}`, {
    params: { language_code: languageCode }
  }),

  // Get the path "trail": ordered story nodes with derived state
  // (locked/available/in_progress/complete), 'you are here', progress + badges.
  getTrail: (pathId, languageCode) => api.get(`/breadcrumbs/paths/${pathId}/trail`, {
    params: { language_code: languageCode }
  }),

  // Get specific story content with glossary - NOW WITH LANGUAGE
  getStory: (storyId, languageCode) => api.get(`/breadcrumbs/stories/${storyId}`, {
    params: { language_code: languageCode }
  }),

  // Update story progress - language goes in data body
  updateStoryProgress: (storyId, data) => api.put(`/breadcrumbs/stories/${storyId}/progress`, data),

  // Get user's progress across all paths - NOW WITH LANGUAGE
  getUserProgress: (languageCode) => api.get('/breadcrumbs/user/progress', {
    params: { language_code: languageCode }
  }),

  // Get user's badges - NOW WITH LANGUAGE
  getUserBadges: (languageCode) => api.get('/breadcrumbs/user/badges', {
    params: { language_code: languageCode }
  })
};

const tagService = {
  // Get all user tags with stats
  getUserTags: (languageCode = null) => {
    const params = languageCode ? { language_code: languageCode } : {};
    return api.get('/tags/my-tags', { params });
  },

  getTagSuggestions: (query, limit = 10, languageCode = null) => {
    const params = { q: query, limit };
    if (languageCode) params.language_code = languageCode;
    return api.get('/tags/suggestions', { params });
  },

  // Create a new custom tag
  createTag: (tagData) => {
    return api.post('/tags/', tagData);
  },

  // Update a tag (name or color)
  updateTag: (tagId, updates) => {
    return api.put(`/tags/${tagId}`, updates);
  },

  // Delete a tag
  deleteTag: (tagId) => {
    return api.delete(`/tags/${tagId}`);
  },

  // Initialize default tags for user
  initializeDefaultTags: () => {
    return api.post('/tags/initialize-defaults');
  }
};

const vocabularyService = {
  // Get vocabulary with optional tag filter
  getVocabularyItems: (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.language_code) {
      params.append('language_code', filters.language_code);
    }
    if (filters.knowledge_level) {
      params.append('knowledge_level', filters.knowledge_level);
    }
    if (filters.tags && filters.tags.length > 0) {
      params.append('tags', filters.tags.join(','));
      params.append('tag_mode', filters.tag_mode || 'OR');
    }

    const queryString = params.toString();
    return api.get(`/vocabulary/${queryString ? '?' + queryString : ''}`);
  },

  // Add vocabulary item with tags
  addVocabularyItem: (data) => {
    // data should include: word, translation, language_code, context, knowledge_level, tags[]
    return api.post('/vocabulary/', data);
  },

  // Update vocabulary item (including tags)
  updateVocabularyItem: (vocabId, updates) => {
    // updates can include: translation, context, knowledge_level, tags[]
    return api.put(`/vocabulary/${vocabId}`, updates);
  },

  // Add tags to existing vocabulary item
  addTagsToVocabulary: (vocabId, tags) => {
    return api.post(`/vocabulary/${vocabId}/tags`, { tags });
  },

  // Remove a tag from vocabulary item
  removeTagFromVocabulary: (vocabId, tagId) => {
    return api.delete(`/vocabulary/${vocabId}/tags/${tagId}`);
  },

  // Get vocabulary stats including tag stats
  getVocabularyStats: (languageCode = null) => {
    const params = languageCode ? { language_code: languageCode } : {};
    return api.get('/vocabulary/stats', { params });
  },

  // Delete vocabulary item
  deleteVocabularyItem: (vocabId) => {
    return api.delete(`/vocabulary/${vocabId}`);
  },

  // Spaced-repetition: get the due review queue (#4)
  getReviewQueue: (languageCode = null, limit = 20) => {
    const params = { limit };
    if (languageCode) params.language_code = languageCode;
    return api.get('/vocabulary/review', { params });
  },

  // Spaced-repetition: grade a reviewed word (grade: 0 Again,1 Hard,2 Good,3 Easy)
  submitReview: (vocabId, grade) => {
    return api.post(`/vocabulary/${vocabId}/review`, { grade });
  },

  // Pronunciation for flashcards -> { job_id }; play via audio.getAudioUrl(job_id).
  getWordAudio: (vocabId) => api.post(`/vocabulary/${vocabId}/audio`, {})
};

const translation = {
  /**
   * Translate a single word or phrase
   * Works for ANY language - no language-specific code needed
   *
   * @param {string} word - Word or phrase to translate
   * @param {string} sourceLanguage - Source language code (e.g., 'fr', 'es', 'de')
   * @param {string} targetLanguage - Target language code (default: 'en')
   * @param {string} context - Optional surrounding text for better accuracy
   * @returns {Promise} - Promise resolving to { word, translation, source_language, target_language }
   */
  translateWord: (word, sourceLanguage, targetLanguage = 'en', context = null) =>
    api.post('/translation/translate-word', {
      word,
      source_language: sourceLanguage,
      target_language: targetLanguage,
      context
    }),

  /**
   * Translate multiple words/phrases in batch
   * More efficient than multiple individual calls
   *
   * @param {string[]} words - Array of words/phrases to translate
   * @param {string} sourceLanguage - Source language code
   * @param {string} targetLanguage - Target language code (default: 'en')
   * @returns {Promise} - Promise resolving to { translations: {...}, source_language, target_language }
   */
  batchTranslate: (words, sourceLanguage, targetLanguage = 'en') =>
    api.post('/translation/batch-translate', {
      words,
      source_language: sourceLanguage,
      target_language: targetLanguage
    })
};

// Idea / prompt library (Discover + Journal). `kind` is 'discover' | 'journal'.
const library = {
  list: (kind) => api.get('/library', { params: { kind } }),
  markUsed: (itemId) => api.post('/library/used', { item_id: itemId }),
};

const journal = {
  getPrompt: (targetLanguage) => api.post('/journal/prompt', { target_language: targetLanguage }),
  analyzeEntry: (text, targetLanguage) => api.post('/journal/analyze', { text, target_language: targetLanguage }),
  getEntries: (params) => api.get('/journal/entries', { params }),
  saveEntry: (data) => api.post('/journal/entries', data),
  updateEntry: (entryId, data) => api.put(`/journal/entries/${entryId}`, data),
  // Read-back audio of the corrected entry -> { job_id }; play via audio.getAudioUrl(job_id).
  getEntryAudio: (entryId) => api.post(`/journal/entries/${entryId}/audio`, {}),
  lookupWord: (word, sourceLang, targetLang) => api.post('/journal/lookup', { word, source_lang: sourceLang, target_language: targetLang }),
  conjugateVerb: (verb, targetLang) => api.post('/journal/conjugate', { verb, target_language: targetLang }),
};

// --- Progress dashboard (#5) ---
const progress = {
  // GET /api/progress?language_code= -> consolidated per-language dashboard
  getProgress: (languageCode) => api.get('/progress', {
    params: { language_code: languageCode }
  }),
};

// --- NEW: Tutor Services ---
const tutor = {
  chat: (message, history, targetLanguage, nativeLanguage, context = null, proficiencyLevel = null) =>
    api.post('/tutor/chat', {
      message,
      history,
      target_language: targetLanguage,
      native_language: nativeLanguage,
      context,
      proficiency_level: proficiencyLevel
    }),
};


// Export all API services
const apiClient = {
  auth,
  content,
  vocabulary:vocabularyService,
  sessions,
  audio: audioService,
  admin,
  tags:tagService,
  breadcrumbs,
  translation,
  journal,
  library,
  tutor,
  progress
};

export default apiClient;